import React from 'react';
import { Button, Grid, InputLabel, MenuItem, OutlinedInput, Paper, Select, Typography, FormHelperText } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import PageHeader from '../../../components/page-header';
import BrazilianNumberInput from '../../../components/brazilian-number-input';
import { FieldError, FormPageFooter, FormSectionTitle } from '../../../components/form-page-parts';
import { AMENDMENT_TYPES, AMENDMENT_STATUSES } from '../../../model/sge/amendment/amendment-enums';
import { parseNumberInput } from '../../../utils/format';
import { formatCodeName } from '../components/code-name-label';
import { useAmendmentCreateEditController } from './context/amendment-create-edit-controller';
import { useAmendmentCreateEditState } from './context/amendment-create-edit-context';
import { validateAmendment } from './components/amendment-form-utils';

export function AmendmentCreateEditComponent() {
    const ctrl = useAmendmentCreateEditController();
    const {
        amendmentRequest: form, parlamentarians, politicalParties,
        budgetUnits, selectedBudgetUnitId, budgetPrograms, selectedBudgetProgramId, budgetActions,
        showValidationErrors, amendmentId, files,
    } = useAmendmentCreateEditState();
    const isEdit = !!amendmentId;
    const errors = showValidationErrors ? validateAmendment(form, { budgetUnitId: selectedBudgetUnitId, budgetProgramId: selectedBudgetProgramId }) : {};

    return (
        <React.Fragment>
            <Grid item xs={12}>
                <PageHeader
                    title={isEdit ? 'Editar Emenda' : 'Nova Emenda'}
                    subtitle={isEdit ? 'Atualize as informações da emenda parlamentar' : 'Cadastre uma nova emenda parlamentar'}
                    breadcrumbs={[{ label: 'Emendas', href: '/emendas' }, { label: isEdit ? 'Editar' : 'Nova' }]}
                />
            </Grid>

            <Grid item xs={12}>
                <Paper sx={{ p: 3 }} elevation={3}>
                    <FormSectionTitle title="Identificação" first />
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={8}>
                            <InputLabel required>Nome</InputLabel>
                            <OutlinedInput fullWidth autoComplete="off" value={form.name || ''} onChange={(e) => ctrl.updateRequest({ name: e.target.value })} error={!!errors.name} />
                            <FieldError message={errors.name} />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <InputLabel required>Exercício</InputLabel>
                            <OutlinedInput fullWidth autoComplete="off" type="number" value={form.year ?? ''} onChange={(e) => ctrl.updateRequest({ year: parseNumberInput(e.target.value) })} error={!!errors.year} />
                            <FieldError message={errors.year} />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <InputLabel required>Status</InputLabel>
                            <Select fullWidth value={form.status ?? ''} onChange={(e) => ctrl.updateRequest({ status: parseNumberInput(e.target.value) })} error={!!errors.status}>
                                <MenuItem value=""><em>Selecionar</em></MenuItem>
                                {AMENDMENT_STATUSES.map((s) => <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>)}
                            </Select>
                            <FieldError message={errors.status} />
                        </Grid>
                    </Grid>

                    <FormSectionTitle title="Vinculação e Valores" />
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={3}>
                            <InputLabel required>Tipo</InputLabel>
                            <Select fullWidth value={form.type ?? ''} onChange={(e) => ctrl.updateRequest({ type: parseNumberInput(e.target.value) })} error={!!errors.type}>
                                <MenuItem value=""><em>Selecionar</em></MenuItem>
                                {AMENDMENT_TYPES.map((t) => <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>)}
                            </Select>
                            <FieldError message={errors.type} />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <InputLabel required>Parlamentar</InputLabel>
                            <Select fullWidth value={form.parlamentarId ?? ''} onChange={(e) => ctrl.handleParlamentarianChange(parseNumberInput(e.target.value))} error={!!errors.parlamentarId}>
                                <MenuItem value=""><em>Selecionar</em></MenuItem>
                                {parlamentarians.map((p) => <MenuItem key={p.id} value={p.id}>{p.personName}</MenuItem>)}
                            </Select>
                            <FieldError message={errors.parlamentarId} />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <InputLabel required>Partido Político</InputLabel>
                            <Select fullWidth value={form.politicalPartyId ?? ''} onChange={(e) => ctrl.updateRequest({ politicalPartyId: parseNumberInput(e.target.value) })} error={!!errors.politicalPartyId}>
                                <MenuItem value=""><em>Selecionar</em></MenuItem>
                                {politicalParties.map((p) => <MenuItem key={p.id} value={p.id}>{p.abbreviation} - {p.name}</MenuItem>)}
                            </Select>
                            <FieldError message={errors.politicalPartyId} />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <InputLabel required>Valor Total</InputLabel>
                            <BrazilianNumberInput
                                fullWidth
                                value={form.totalAmount || 0}
                                onChange={(totalAmount) => ctrl.updateRequest({ totalAmount })}
                                decimals={2}
                                prefix="R$"
                                placeholder="0,00"
                                error={!!errors.totalAmount}
                            />
                            <FieldError message={errors.totalAmount} />
                        </Grid>
                    </Grid>

                    <FormSectionTitle title="Classificação Orçamentária" />
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                            <InputLabel required>Unidade Orçamentária</InputLabel>
                            <Select fullWidth value={selectedBudgetUnitId ?? ''} onChange={(e) => ctrl.handleBudgetUnitChange(parseNumberInput(e.target.value))} error={!!errors.budgetUnitId}>
                                <MenuItem value=""><em>Selecionar</em></MenuItem>
                                {budgetUnits.map((u) => <MenuItem key={u.id} value={u.id}>{formatCodeName(u)} ({u.year})</MenuItem>)}
                            </Select>
                            <FieldError message={errors.budgetUnitId} />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <InputLabel required>Programa Orçamentário</InputLabel>
                            <Select fullWidth disabled={!selectedBudgetUnitId} value={selectedBudgetProgramId ?? ''} onChange={(e) => ctrl.handleBudgetProgramChange(parseNumberInput(e.target.value))} error={!!errors.budgetProgramId}>
                                <MenuItem value=""><em>Selecionar</em></MenuItem>
                                {budgetPrograms.map((p) => <MenuItem key={p.id} value={p.id}>{formatCodeName(p)}</MenuItem>)}
                            </Select>
                            {selectedBudgetUnitId ? <FieldError message={errors.budgetProgramId} /> : <FormHelperText>Selecione a Unidade Orçamentária</FormHelperText>}
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <InputLabel required>Ação Orçamentária</InputLabel>
                            <Select fullWidth disabled={!selectedBudgetProgramId} value={form.budgetActionId ?? ''} onChange={(e) => ctrl.updateRequest({ budgetActionId: parseNumberInput(e.target.value) })} error={!!errors.budgetActionId}>
                                <MenuItem value=""><em>Selecionar</em></MenuItem>
                                {budgetActions.map((a) => <MenuItem key={a.id} value={a.id}>{formatCodeName(a)}</MenuItem>)}
                            </Select>
                            {selectedBudgetProgramId ? <FieldError message={errors.budgetActionId} /> : <FormHelperText>Selecione o Programa Orçamentário</FormHelperText>}
                        </Grid>
                    </Grid>

                    <FormSectionTitle title="Informações Adicionais" />
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <InputLabel required>Descrição do Objeto</InputLabel>
                            <OutlinedInput fullWidth multiline rows={2} value={form.objectDescription || ''} onChange={(e) => ctrl.updateRequest({ objectDescription: e.target.value })} error={!!errors.objectDescription} />
                            <FieldError message={errors.objectDescription} />
                        </Grid>
                        <Grid item xs={12}>
                            <InputLabel>Base Legal</InputLabel>
                            <OutlinedInput fullWidth multiline rows={2} value={form.legalBasis || ''} onChange={(e) => ctrl.updateRequest({ legalBasis: e.target.value })} />
                        </Grid>
                        <Grid item xs={12}>
                            <InputLabel>Finalidade Pública</InputLabel>
                            <OutlinedInput fullWidth multiline rows={2} value={form.publicPurpose || ''} onChange={(e) => ctrl.updateRequest({ publicPurpose: e.target.value })} />
                        </Grid>
                        <Grid item xs={12}>
                            <InputLabel>Justificativa Institucional</InputLabel>
                            <OutlinedInput fullWidth multiline rows={2} value={form.institutionalJustification || ''} onChange={(e) => ctrl.updateRequest({ institutionalJustification: e.target.value })} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <InputLabel>Público-Alvo</InputLabel>
                            <OutlinedInput fullWidth autoComplete="off" value={form.targetAudience || ''} onChange={(e) => ctrl.updateRequest({ targetAudience: e.target.value })} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <InputLabel>Local de Execução</InputLabel>
                            <OutlinedInput fullWidth autoComplete="off" value={form.executionLocation || ''} onChange={(e) => ctrl.updateRequest({ executionLocation: e.target.value })} />
                        </Grid>
                    </Grid>

                    {!isEdit && (
                        <React.Fragment>
                            <FormSectionTitle title="Anexos" description="Após salvar, os anexos são gerenciados na aba Anexos do detalhe da emenda" />
                            <Button variant="outlined" component="label" startIcon={<AttachFileIcon />}>
                                Selecionar arquivos ({files.length})
                                <input type="file" multiple hidden onChange={ctrl.handleFilesChange} />
                            </Button>
                            {files.length > 0 && (
                                <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                                    {files.map((f) => f.name).join(', ')}
                                </Typography>
                            )}
                        </React.Fragment>
                    )}

                    <FormPageFooter onCancel={ctrl.handleCancel} onSave={ctrl.handleSave} isSaving={ctrl.isSaving} />
                </Paper>
            </Grid>
        </React.Fragment>
    );
}
